import { Grid } from "@material-ui/core";
import React, { Dispatch, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
    BatchDeleteDelegeeWarning, BatchRevokeParkingRightsWarning, BatchRevokeVehicleAssignmentWarning,
    DeleteDelegeeWarning, DeleteEntryRightDelegeeWarning, DeleteVehiclesAssignmentWarning,
    EntryRightForm,
    MultipleVehiclesForm, MultipleVehiclesUploadView, ParkingRightsForm,
    RevokeEntryRightWarning,
    RevokeParkingRightsWarning,
    RevokeVehicleAssignmentWarning,
    useDataTableContext, VehicleForm
} from "../../../components";
import { DataTableContextFilters } from "../../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FilterType } from "../../../components/DataTable/DataTableFilters/DataTableFilters";
import EntryRightMultipleDelegeesForm from "../../../components/EntryRightMultipleDelegeesForm/EntryRightMultipleDelegeesForm";
import ParkingRightMultipleDelegeesForm from "../../../components/ParkingRightMultipleDelegeesForm/ParkingRightMultipleDelegeesForm";
import { FmpFilters } from "../../../constants/filtering.constants";
import { useAppContext, useFleetManagerContext, useMyFleetContext } from "../../../context/hooks";
import useImportDelegationsContext from "../../../context/hooks/useImportDelegationsContext";
import ImportVehiclesContext from "../../../context/ImportVehiclesContext";
import { useOnBulkImportFinishedCallback } from "../../../hooks/useOnBulkImportFinishedCallback";
import { ForFleetManager, ParkingProductType, WithFiltersAndPaging } from "../../../models";
import { ContractType } from "../../../models/ParkingProductModel";
import { getEntryRightDelegeesMyFleet } from "../../../services/entryRights.service";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
    getEntryRightDelegeesMyFleetOverviewThunk,
    toggleShowDeleteEntryRightsDelegeesAction,
    toggleShowRevokeEntryRightsAction
} from "../../../store/actions/entryRights.actions";
import environmentActions from "../../../store/actions/environment.actions";
import * as parkingProductsThunks from "../../../store/actions/parkingProducts.thunks";
import { RootReducer } from "../../../store/reducers";
import ActionDrawer from "../../../ui/ActionDrawer/ActionDrawer";
import Section from "../../../ui/Section/Section";
import {
    selectDoShowDva,
    selectEnableDelegationsScheduling,
    selectEnableFleetVehicleScheduling,
    selectHasPedestrianProducts,
    isEntryRightsMyProductsRowKey,
    selectUseBulkVehicleAssignments,
    selectUseDirectVehicleAssignment,
    TabOption,
    EntryRightProductKey,
    isEntryRightsMyProductsRowKeyArray
} from "../MyProducts/ParkingRightsDataTable/common";
import TableSwitcher from "../MyProducts/ParkingRightsDataTable/TableSwitcher";
import DelegeesDataTable from "./DelegeesDataTable/DelegeesDataTable";
import { getMessage, getSelectedParkingRightIds, getSelectedParkingRightIdSingle as getSelectedParkingRightIdsSingle } from "./MyFleetHelpers";
import VehiclesTableMyFleet from "./VehiclesTableMyFleet/VehiclesTableMyFleet";


interface IProps extends StateProps, DispatchProps { }

const MyFleet = ({
    getParkingProducts,
    getLocations,
    delegees }: IProps) => {
    const { appState } = useAppContext();

    const {
        fleetManagerState,
        toggleAddDelegee,
        toggleAssignVehicle,
        toggleMultipleVehicles,
        toggleBatchRevokeParkingRightsWarning,
        toggleRevokeParkingRightsWarning,
        toggleEditDelegee,
        toggleEditVehicleAssignment,
        toggleDeleteDelegeeWarning,
        toggleRevokeVehicleAssignmentsWarning,
        toggleBatchRevokeVehicleAssignmentsWarning,
        toggleDeleteVehicleAssignmentsWarning,
        toggleBatchDeleteDelegationsWarning,
        toggleEntryRightInvitationForm,
        setSelectedParkingRightIds,
        resetContext: resetFleetManagerContext,
    } = useFleetManagerContext();

    const [tab, setTab] = useState(TabOption.FleetMembers);
    const [vehicleRefresh, setVehicleRefresh] = useState<{
        fetchKey: number;
        excludedVehicleAssignmentIds: string[];
    }>({
        fetchKey: 0,
        excludedVehicleAssignmentIds: [],
    });
    const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);
    const { setSorting } = useMyFleetContext();
    const {
        resetContext,
        state: dataTable,
        resetPageNumber,
        resetPageSize,
    } = useDataTableContext();
    const {
        myFleetContextState
    } = useMyFleetContext();

    const toggleRevokeEntryRightsWarning = () => dispatch(toggleShowRevokeEntryRightsAction());
    const toggleDeleteEntryRightsDelegeesWarning = () => dispatch(toggleShowDeleteEntryRightsDelegeesAction());

    const { importDelegationsState } = useImportDelegationsContext();


    const useDirectVehicleAssignment = useAppSelector(selectUseDirectVehicleAssignment);
    const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);
    const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);
    const enableFleetVehicleScheduling = useAppSelector(selectEnableFleetVehicleScheduling);
    const showRevokePedestriansWarning = useAppSelector((state) => state.entryRights.showRevokePedestriansWarning);
    const showDeleteEntryRightDelegeesWarning = useAppSelector((state) => state.entryRights.showDeleteEntryRightDelegeesWarning);

    const { t } = useTranslation(["myFleet", "globals", "dataTable"]);
    const getMetaTitle = () =>
        fleetManagerState.showDelegeeDetails ? t("addDelegee") : t("myFleet");

    const fetchParkingProducts = () => {
        getParkingProducts(appState.user.seasonTicketOwnerCrmId as string, appState.selectedLanguage);
    };

    const dispatch = useAppDispatch();

    useEffect(() => {
        setFeatureFlagsLoaded(false);
        void (async () => {
            fetchParkingProducts();
            getLocations(appState.user.seasonTicketOwnerCrmId!, appState.selectedLanguage);
            await dispatch(environmentActions.getFeatureFlagsAndDynamicConfigurations(appState.user.seasonTicketOwnerCrmId!));
            setFeatureFlagsLoaded(true);
        })();
        
        return () => {
            resetFleetManagerContext();
        };
        // eslint-disable-next-line
    }, [appState.user.seasonTicketOwnerCrmId]);

    // second useEffect runs when featureFlags are fetched and enableFleetVehicleScheduling flag is set, 
    // Because to fetch vehicle assignments with the correct endpoint 
    useEffect(() => {
        if (!featureFlagsLoaded) {
            return;
        }

        const request = createRequest();
        if (enableDelegationsScheduling) {
            dispatch(getEntryRightDelegeesMyFleetOverviewThunk({
                request,
                productContractTypeFilter: { contractType: ContractType.SeasonTicket, productType: ParkingProductType.PedestrianAccess }
            }));
        } else {
            dispatch(getEntryRightDelegeesMyFleet(request, { contractType: ContractType.SeasonTicket, productType: ParkingProductType.PedestrianAccess }));
        }
        // eslint-disable-next-line
    }, [featureFlagsLoaded, enableDelegationsScheduling, enableFleetVehicleScheduling, appState.user.seasonTicketOwnerCrmId]);

    const onDelegeeFormClose = () => {
        if (fleetManagerState.isEditDelegeeMode) {
            toggleEditDelegee("");
        } else {
            toggleAddDelegee();
        }
    };

    const onVehicleFormClose = () => {
        if (fleetManagerState.isEditVehicleAssignmentMode) {
            toggleEditVehicleAssignment();
        } else {
            toggleAssignVehicle();
        }
    };

    const onMultipleVehiclesClose = () => {
        toggleMultipleVehicles();
    };

    const onPedestrianInvitationFormClose = () => {
        toggleEntryRightInvitationForm();
    };

    const getFilters = (): DataTableContextFilters[] => [
        {
            key: FmpFilters.sortBy,
            type: FilterType.QueryParam,
            value: myFleetContextState.sortBy,
        },
        {
            key: FmpFilters.orderDesc,
            type: FilterType.QueryParam,
            value: myFleetContextState.orderDesc?.toString(),
        },
        ...(dataTable.filters as DataTableContextFilters[])
    ];

    const createRequest = (): WithFiltersAndPaging<ForFleetManager> => ({
        entity: {
            seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
            language: appState.selectedLanguage,
        },
        pagination: {
            pageSize: dataTable.pageSize!,
            pageNumber: dataTable.pageNumber!,
        },
        filters: getFilters(),
    });

    const onRefresh = () => {
        let request = createRequest();

        if (tab === TabOption.PedestrianRights) {
            if (enableDelegationsScheduling) {
                dispatch(getEntryRightDelegeesMyFleetOverviewThunk({
                    request,
                    productContractTypeFilter: { contractType: ContractType.SeasonTicket, productType: ParkingProductType.PedestrianAccess }
                }));
            } else {
                dispatch(getEntryRightDelegeesMyFleet(request, { contractType: ContractType.SeasonTicket, productType: ParkingProductType.PedestrianAccess }));
            }
        }

        if (tab === TabOption.Vehicles) {
            refreshVehicles();
        }
    };

    useOnBulkImportFinishedCallback(onRefresh);

    const getSelectedPedestrians = (): Array<string> | Array<EntryRightProductKey> =>
        fleetManagerState.selectedRowKeys?.length > 0
            ? fleetManagerState.selectedRowKeys
            : [fleetManagerState.selectedDelegeeId];

    const selectedDelegeeIds = useMemo(() => {
        const { selectedRowKeys, selectedDelegeeId } = fleetManagerState;
        if (fleetManagerState.selectedRowKeys?.length > 0) {
            if (isEntryRightsMyProductsRowKeyArray(fleetManagerState.selectedRowKeys)) {
                return [ ... new Set(fleetManagerState.selectedRowKeys.map((rowKey) => rowKey.delegeeId))];
            }

            return selectedRowKeys;
        }
        return [selectedDelegeeId];
    }, [fleetManagerState.selectedRowKeys, fleetManagerState.selectedDelegeeId]);

    const getDeleteDelegeeWarningText = (parkingRightsCount: number): string =>
        parkingRightsCount > 0
            ? t("deleteDelegeeWarning.warning", { count: parkingRightsCount })
            : t("deleteDelegeeWarning.warningNoPRs");

    const getDeleteDelegeeWarningDetails = (parkingRightsCount: number): string =>
        parkingRightsCount > 0
            ? t("deleteDelegeeWarning.details", { count: parkingRightsCount })
            : t("deleteDelegeeWarning.detailsNoPRs");

    const getRevocationEmailText = (): string =>
        t("deleteDelegeeWarning.sendRevocationEmail");

    const getBatchDeleteWarning = (): string =>
        getMessage(
            t,
            "deleteDelegeeWarning.warningBatch",
            selectedDelegeeIds.length,
            "deleteDelegeeWarning.warningBatchPlural"
        );

    const getBatchDeleteDescription = (): string =>
        getMessage(
            t,
            "deleteDelegeeWarning.detailsBatch",
            selectedDelegeeIds.length,
            "deleteDelegeeWarning.detailsBatchPlural"
        );
    
    const refreshVehicles = (excludedIds: string[] = []) => {
        setVehicleRefresh((currentRefresh) => ({
            fetchKey: currentRefresh.fetchKey + 1,
            excludedVehicleAssignmentIds: excludedIds,
        }));
    };

    const selectedParkingRightIds = getSelectedParkingRightIds(
        fleetManagerState,
        dataTable,
        delegees
    );

    const selectedParkingRightIdsSingle = getSelectedParkingRightIdsSingle(
        fleetManagerState,
        dataTable,
        delegees
    );

    const onAddClickHandler = () => {
        setSelectedParkingRightIds([]);
        toggleAddDelegee();
    };

    const onAssignVehicleClickHandler = () => {
        toggleAssignVehicle();
    };

    const onWhitelistVehiclesClickHandler = () => {
        toggleMultipleVehicles();
    };

    useEffect(() => {
        if (useBulkVehicleAssignments) {
            resetPageNumber();
            resetPageSize();
            setSelectedParkingRightIds([]);
            resetContext();
            setSorting(enableFleetVehicleScheduling ? "default" : "assignedDate", true);
            setTab(TabOption.Vehicles);
        }
    }, [useBulkVehicleAssignments]);

    const onTabSelected = (newTab: TabOption): void => {
        if (tab !== newTab) {
            resetPageNumber();
            resetPageSize();
            setSelectedParkingRightIds([]);

            let sorting = "";
            switch (newTab) {
                case TabOption.FleetMembers:
                    sorting = "invitationDate";
                    break;
                case TabOption.PedestrianRights:
                    sorting = "invitationDate";
                    break;
                case TabOption.Vehicles:
                    sorting = enableFleetVehicleScheduling ? "default" : "assignedDate";
                    break;
            }

            resetContext();
            setSorting(sorting, true);
        }
        setTab(newTab);
    };

    const vehicleAssignmentsUniqueCount = useAppSelector((state) => state.parkingProducts.vehicleAssignmentsMyFleetOverview.totalUniqueVehicles); 
    const vehicleAssignmentsCount = useAppSelector((state) => state.parkingProducts.vehicleAssignmentsMyFleet.totalRecords); 
    const fleetMembersCount = useAppSelector((state) => enableDelegationsScheduling ? state.entryRights.myFleetOverview.parkingRights.totalUniqueDelegees : state.parkingProducts.delegees.totalRecords);
    const entryRightsTotalCount = useAppSelector((state) =>
        enableDelegationsScheduling
            ? state.entryRights.myFleetOverview.pedestrians.totalUniqueDelegees
            : state.entryRights.myFleet.delegees.totalRecords);
    const hasUnlimitedEntryRights = useAppSelector((state) => state.parkingProducts.hasUnlimitedEntryRights);
    const hasPedestrianProducts = useAppSelector(selectHasPedestrianProducts);
    const hidePedestrianRightsOption = hasUnlimitedEntryRights || !hasPedestrianProducts;
    const showDva = useAppSelector(selectDoShowDva);
    const hideDva = !showDva;
    const switcher = (
        <TableSwitcher
            selected={tab}
            onTabSelected={onTabSelected}
            options={[
                {
                    value: TabOption.FleetMembers,
                    label: t("tableSwitcher:parkingRights", { count: fleetMembersCount ?? 0 }),
                    hide: hasUnlimitedEntryRights,
                },
                {
                    value: TabOption.PedestrianRights,
                    label: t("tableSwitcher:pedestrianRights", { count: entryRightsTotalCount ?? 0 }),
                    hide: hidePedestrianRightsOption,
                },
                {
                    value: TabOption.Vehicles,
                    label: t("tableSwitcher:vehicles", { count: enableFleetVehicleScheduling ? 
                        vehicleAssignmentsUniqueCount ?? 0 : vehicleAssignmentsCount ?? 0 }),
                    hide: hideDva,
                },
            ]}
        />
    );

    const selectedBulkParkingProductIsGoBusiness = importDelegationsState.checkParkingRights?.length > 0 &&
        importDelegationsState.checkParkingRights.every(cpr => cpr.right?.contractType === ContractType.GoBusiness
            || cpr.right?.productType === ParkingProductType.PedestrianAccess);

    const showEntryRightMultipleDelegeesform = selectedBulkParkingProductIsGoBusiness;

    const selectedBulkParkingProductIsSeasonTicketParkingRight = importDelegationsState.checkParkingRights?.length > 0 &&
        importDelegationsState.checkParkingRights.every(cpr => cpr.right?.contractType === ContractType.SeasonTicket
            && cpr.right?.productType === ParkingProductType.ParkingRights);

    const showParkingProductMultipleDelegeesform = selectedBulkParkingProductIsSeasonTicketParkingRight;

    return (
        <React.Fragment>
            <Helmet>
                <title>{`Q-Park | ${getMetaTitle()}`}</title>
            </Helmet>
            <ActionDrawer
                open={fleetManagerState.showDelegeeDetails}
                onClose={onDelegeeFormClose}
                className="delegee-form-drawer"
            >
                {!fleetManagerState.dragAndDropView ? (
                    enableDelegationsScheduling ? (
                        <EntryRightForm
                            onClose={onDelegeeFormClose}
                            refreshHandler={onRefresh}
                            delegationMode="parkingRights"
                        />
                    ) : (
                        <ParkingRightsForm
                            onClose={onDelegeeFormClose}
                            refreshHandler={onRefresh}
                        />
                    )
                ) : (
                    (fleetManagerState.showDelegeeDetails && showParkingProductMultipleDelegeesform &&
                        <ParkingRightMultipleDelegeesForm onClose={onDelegeeFormClose} translationContextName="parkingRightsForm" />)
                    || (fleetManagerState.showDelegeeDetails && showEntryRightMultipleDelegeesform &&
                        <EntryRightMultipleDelegeesForm onClose={onDelegeeFormClose} translationContextName="parkingRightsForm" />)
                )}
            </ActionDrawer>
            <ActionDrawer
                open={fleetManagerState.showDeleteDelegeeWarning}
                onClose={toggleDeleteDelegeeWarning}
            >
                <DeleteDelegeeWarning
                    getWarningText={getDeleteDelegeeWarningText}
                    getWarningDetails={getDeleteDelegeeWarningDetails}
                    getRevocationEmailText={getRevocationEmailText}
                    onRefresh={onRefresh}
                />
            </ActionDrawer>
            <ActionDrawer
                open={fleetManagerState.showBatchRevokeParkingRightsWarning}
                onClose={toggleBatchRevokeParkingRightsWarning}
            >
                <BatchRevokeParkingRightsWarning
                    onRefresh={onRefresh}
                    selectedParkingRightIds={selectedParkingRightIds}
                />
            </ActionDrawer>
            <ActionDrawer
                open={fleetManagerState.showRevokeParkingRightsWarning}
                onClose={toggleRevokeParkingRightsWarning}
            >
                <RevokeParkingRightsWarning
                    onRefresh={onRefresh}
                    selectedParkingRightIds={selectedParkingRightIdsSingle}
                />
            </ActionDrawer>
            <ActionDrawer
                open={fleetManagerState.showBatchRevokeParkingVehicleAssignment}
                onClose={toggleBatchRevokeVehicleAssignmentsWarning}
            >
                <BatchRevokeVehicleAssignmentWarning
                    onRefresh={() => {
                        // my fleet vehicles are reduced client side when revoked
                    }}
                    selectedVehicleAssignmentKeys={fleetManagerState.selectedRowKeys}
                />
            </ActionDrawer>
            <ActionDrawer
                open={fleetManagerState.showRevokeParkingVehicleAssignment}
                onClose={toggleRevokeVehicleAssignmentsWarning}
            >
                <RevokeVehicleAssignmentWarning
                    onRefresh={() => {
                        // my fleet vehicles are reduced client side when revoked
                    }}
                />
            </ActionDrawer>
            <ActionDrawer
                open={showRevokePedestriansWarning}
                onClose={toggleRevokeEntryRightsWarning}
            >
                <RevokeEntryRightWarning
                    selectedPedestrianIds={getSelectedPedestrians()}
                    revokeProductType={tab === TabOption.PedestrianRights ? 'pedestrian' : 'parking'}
                />
            </ActionDrawer>
            <ActionDrawer
                open={fleetManagerState.showDeleteVehiclesAssignment}
                onClose={toggleDeleteVehicleAssignmentsWarning}
            >
                <DeleteVehiclesAssignmentWarning
                    onRefresh={(toExclude) => {
                        refreshVehicles(toExclude?.map(x => x.vehicleAssignmentId));
                    }}
                    selectedVehicleAssignmentKeys={fleetManagerState.selectedRowKeys}
                />
            </ActionDrawer>
            <ActionDrawer
                open={fleetManagerState.showBatchDeleteDelegationsWarning}
                onClose={toggleBatchDeleteDelegationsWarning}
            >
                <BatchDeleteDelegeeWarning
                    options={{
                        warning: getBatchDeleteWarning(),
                        warningDescription: getBatchDeleteDescription(),
                        revocationEmailText: getRevocationEmailText(),
                    }}
                    onRefresh={onRefresh}
                    productType={tab === TabOption.PedestrianRights ? ParkingProductType.PedestrianAccess : ParkingProductType.ParkingRights}
                />
            </ActionDrawer>
            <ActionDrawer
                open={showDeleteEntryRightDelegeesWarning}
                onClose={toggleDeleteEntryRightsDelegeesWarning}
            >
                <DeleteEntryRightDelegeeWarning
                    selectedPedestrianIds={selectedDelegeeIds}
                    myFleet
                />
            </ActionDrawer>
            <ImportVehiclesContext>
                <ActionDrawer open={fleetManagerState.showAssignVehicle} onClose={onVehicleFormClose}>
                    <VehicleForm onClose={onVehicleFormClose} />
                </ActionDrawer>
                <ActionDrawer open={fleetManagerState.showWhitelistVehicles && !fleetManagerState.vehicleDragAndDropView} onClose={onMultipleVehiclesClose}>
                    <MultipleVehiclesForm onClose={onMultipleVehiclesClose} />
                </ActionDrawer>
                <ActionDrawer open={fleetManagerState.vehicleDragAndDropView}>
                    <MultipleVehiclesUploadView
                        onRefresh={() => refreshVehicles()} />
                </ActionDrawer>
                <ActionDrawer
                    open={fleetManagerState.showPedestrianInvitationForm}
                    onClose={onPedestrianInvitationFormClose}>
                    <EntryRightForm onClose={onPedestrianInvitationFormClose} delegationMode="pedestrian" />
                </ActionDrawer>
                {fleetManagerState.showPedestrianInvitationForm && <EntryRightMultipleDelegeesForm onClose={onPedestrianInvitationFormClose} translationContextName="pedestrianInvitationForm" />}
            </ImportVehiclesContext>
            <Grid container direction="row" alignItems="center">
                <Section>
                    {tab === TabOption.FleetMembers &&
                        <DelegeesDataTable
                            subHeading={switcher}
                            productType={ParkingProductType.ParkingRights} />}
                    {useDirectVehicleAssignment && tab === TabOption.Vehicles &&
                        <VehiclesTableMyFleet
                            onRevokeClicked={toggleBatchRevokeVehicleAssignmentsWarning}
                            onDeleteClicked={toggleDeleteVehicleAssignmentsWarning}
                            onAssignVehicleClicked={onAssignVehicleClickHandler}
                            onInviteFleetMemberClicked={onAddClickHandler}
                            onWhitelistVehiclesClicked={onWhitelistVehiclesClickHandler}
                            fetchKey={vehicleRefresh.fetchKey}
                            excludedVehicleAssignmentIds={vehicleRefresh.excludedVehicleAssignmentIds}
                            subHeading={switcher} />}
                    {!hidePedestrianRightsOption && tab === TabOption.PedestrianRights &&
                        <DelegeesDataTable
                            subHeading={switcher}
                            productType={ParkingProductType.PedestrianAccess}
                        />}
                </Section>
            </Grid>
        </React.Fragment>
    );
};

const mapStateToProps = (state: RootReducer) => {
    const { delegees } = state.parkingProducts;

    return { delegees };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
    getParkingProducts: (seasonTicketOwnerCrmId: string, location: string) =>
        dispatch(parkingProductsThunks.getParkingProducts({ seasonTicketOwnerCrmId, location })),
    getLocations: (seasonTicketOwnerCrmId: string, language: string) =>
        dispatch(parkingProductsThunks.getLocatedParkingProductsThunk({ seasonTicketOwnerCrmId, language })),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(MyFleet);
